/**
 * Google Analytics 4 loader — GDPR-safe.
 *
 * gtag.js is injected ONLY after the user grants analytics consent (see
 * ConsentContext / CookieConsentBanner). No request is made to Google before
 * consent. The whole module is inert unless `VITE_GA_MEASUREMENT_ID` is set,
 * so it ships dark until the id is configured in the environment.
 *
 * Page views are sent manually (send_page_view: false) so SPA route changes are
 * reported exactly once per navigation by <GoogleAnalytics/>.
 */

// Read via a widened cast so this compiles without augmenting ImportMetaEnv.
const GA_ID = (import.meta.env as Record<string, string | undefined>)
  .VITE_GA_MEASUREMENT_ID;

let loaded = false;

/** True only when a measurement id is configured. */
export function isGaConfigured(): boolean {
  return Boolean(GA_ID);
}

/**
 * Inject gtag.js and configure GA4 exactly once. Safe to call repeatedly.
 * Call only after the user has granted analytics consent.
 */
export function loadGoogleAnalytics(): void {
  if (loaded || !GA_ID || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  loaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  const w = window as unknown as {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer || [];
  // gtag.js only executes commands pushed as real `arguments` objects — a
  // plain array is silently discarded, which disables all measurement. Do not
  // "modernize" this into an arrow function with rest args.
  w.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  };
  w.gtag('js', new Date());
  w.gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });
}

/** Send a page_view for the current SPA path. No-op until GA is loaded. */
export function trackPageView(path: string): void {
  if (!loaded || !GA_ID || typeof window === 'undefined') return;
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (!w.gtag) return;
  w.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  });
}
